import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Phone, Globe, User, Menu, X, Building2, Users, MapPin, Lock, Clock, Headphones, Calendar, Shield, Settings, Image } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const navigationItems = [
    {
      id: 'office-space',
      label: 'Office Space',
      icon: Building2,
      route: '/office-space'
    },
    {
      id: 'coworking',
      label: 'Coworking',
      icon: Users,
      route: '/coworking'
    },
    {
      id: 'virtual-offices',
      label: 'Virtual Offices',
      icon: Globe,
      route: '/virtual-office'
    },
    {
      id: 'meeting-rooms',
      label: 'Meeting Rooms',
      icon: Users,
      route: '/meeting-rooms'
    },
    {
      id: 'business-address',
      label: 'Business Address',
      icon: MapPin,
      route: '/business-addresses'
    },

    // {
    //   id: 'private-offices',
    //   label: 'Private Offices',
    //   icon: Lock,
    //   route: '/private-offices'
    // },
    {
      id: 'day-offices',
      label: 'Day Offices',
      icon: Lock,
      route: '/day-offices'
    },

  ];

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto">
        {/* Main header */}
        <div className="px-4 py-2">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src="/Laksh Space Logo Header .png"
                alt="Laksh Space"
                className="h-16 w-auto"
                onError={(e) => {
                  // Fallback to text logo if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="text-3xl font-bold text-gray-900 hidden">
                Laksh <span className="text-[#e30613]">Space</span>
              </div>
            </Link>

            {/* Right side - Contact and Menu */}
            <div className="flex items-center space-x-4">
              {/* Contact Number - Desktop */}
              <a
                href="tel:+919972235566"
                className="hidden md:flex items-center space-x-2 bg-[#e30613] hover:bg-[#c1050f] text-white px-3 py-1.5 rounded-lg transition-colors"
              >
                <Phone size={14} />
                <span className="font-medium text-sm">+91 99722 35566</span>
              </a>

              {/* Menu button - Both Mobile and Desktop */}
              <div className="relative">
                <button 
                  className="p-2 text-gray-700 hover:text-[#e30613] transition-colors"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Bar */}
        <div className="border-t border-gray-200 bg-white">
          <div className="px-4">
            <div className="flex items-center justify-center overflow-x-auto">
              {/* Navigation Items */}
              <nav className="flex items-center space-x-0">
                {navigationItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Link
                      key={item.id}
                      to={item.route}
                      className="flex flex-col items-center justify-center px-4 py-3 text-gray-700 hover:text-[#e30613] hover:bg-gray-50 transition-colors group min-w-[120px]"
                    >
                      <IconComponent 
                        size={22} 
                        strokeWidth={1.5}
                        className="mb-1 group-hover:text-[#e30613]" 
                      />
                      <span className="text-xs font-medium text-center leading-tight">
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
              </nav>

              {/* Scroll Arrow (for mobile) */}
              <button className="md:hidden p-2 text-gray-400">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M6 4l4 4-4 4V4z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Dropdown Menu (Right-aligned) */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-25 z-40"
              onClick={() => setMobileMenuOpen(false)}
            ></div>
            
            {/* Dropdown Menu */}
            <div className="fixed top-0 right-0 w-full sm:w-[500px] md:w-[600px] lg:w-[450px] h-screen bg-white border-l border-gray-200 shadow-2xl z-50 flex flex-col">
            {/* Close Button */}
            <div className="absolute top-4 right-4 z-10">
              <button 
                className="p-3 text-gray-700 hover:text-[#e30613] transition-colors bg-gray-100 hover:bg-gray-200 rounded-full"
                onClick={() => setMobileMenuOpen(false)}
              >
                <X size={28} strokeWidth={2} />
              </button>
            </div>

            {/* Dropdown Header */}
            <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-[#e30613] to-[#c1050f] mt-20">
              <h3 className="text-2xl font-black text-white">Navigation Menu</h3>
            </div>

            {/* Navigation - Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <nav className="py-8 pb-32">
              {/* Main Pages */}
              <div className="px-6 py-4">
                <div className="text-lg font-bold text-gray-900 uppercase tracking-wide px-4 py-3 mb-4 bg-gray-100 rounded-lg">
                  Main Pages
                </div>
                <Link
                  to="/"
                  className="flex items-center space-x-4 px-6 py-4 text-gray-800 hover:text-[#e30613] hover:bg-gray-50 rounded-xl transition-all duration-300 hover:scale-105 mb-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="text-2xl font-bold">Home</span>
                </Link>
                <Link
                  to="/products"
                  className="flex items-center space-x-4 px-6 py-4 text-gray-800 hover:text-[#e30613] hover:bg-gray-50 rounded-xl transition-all duration-300 hover:scale-105 mb-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="text-2xl font-bold">Services</span>
                </Link>
                <Link
                  to="/gallery"
                  className="flex items-center space-x-4 px-6 py-4 text-gray-800 hover:text-[#e30613] hover:bg-gray-50 rounded-xl transition-all duration-300 hover:scale-105 mb-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="text-2xl font-bold">Gallery</span>
                </Link>
                <Link
                  to="/about"
                  className="flex items-center space-x-4 px-6 py-4 text-gray-800 hover:text-[#e30613] hover:bg-gray-50 rounded-xl transition-all duration-300 hover:scale-105 mb-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="text-2xl font-bold">About</span>
                </Link>
                <Link
                  to="/contact"
                  className="flex items-center space-x-4 px-6 py-4 text-gray-800 hover:text-[#e30613] hover:bg-gray-50 rounded-xl transition-all duration-300 hover:scale-105 mb-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="text-2xl font-bold">Contact</span>
                </Link>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 mx-6 my-6"></div>
              
              {/* Workspace Categories */}
              <div className="px-6 py-4">
                <div className="text-lg font-bold text-gray-900 uppercase tracking-wide px-4 py-3 mb-4 bg-gray-100 rounded-lg">
                  Workspace Solutions
                </div>
                {navigationItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Link
                      key={item.id}
                      to={item.route}
                      className="flex items-center space-x-4 px-6 py-4 text-gray-800 hover:text-[#e30613] hover:bg-gray-50 rounded-xl transition-all duration-300 hover:scale-105 mb-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <IconComponent size={24} strokeWidth={2} />
                      <span className="text-xl font-bold">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
              </nav>
            </div>

            {/* Contact Section - Fixed at Bottom */}
            <div className="px-8 py-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="space-y-3">
                <a
                  href="tel:+919972235566"
                  className="flex items-center justify-center space-x-3 bg-[#e30613] hover:bg-[#c1050f] text-white px-6 py-3 rounded-xl transition-all duration-300 hover:scale-105 w-full shadow-lg"
                >
                  <Phone size={18} strokeWidth={2} />
                  <span className="text-lg font-bold">+91 99722 35566</span>
                </a>
              </div>
            </div>
          </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;